import React, { useMemo, useState } from 'react';
import { productImageUrl } from './AiLookResults';
import useModalEscape from './useModalEscape';

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function truncate(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let next = text;
  while (next.length > 1 && ctx.measureText(`${next}...`).width > maxWidth) next = next.slice(0, -1);
  return `${next}...`;
}

async function fetchBlob(url) {
  const res = await fetch(url, { cache: 'no-cache', mode: 'cors' });
  if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
  return URL.createObjectURL(await res.blob());
}

function bestCandidates(match) {
  return (match?.items || [])
    .map((item) => {
      const candidates = ['exact', 'similar', 'budget', 'luxury'].flatMap((lane) => item.candidates?.[lane] || []);
      return candidates[0] ? { item, candidate: candidates[0] } : null;
    })
    .filter(Boolean)
    .slice(0, 4);
}

export default function LookShareCard({ event, match, formatPrice, totalLabel, t, onClose }) {
  useModalEscape(true, onClose);

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const picks = useMemo(() => bestCandidates(match), [match]);
  const celebrity = event?.celebrity_name || 'Outfit Detective';
  const imageUrl = event?.image_url;

  async function downloadStory() {
    if (downloading || !imageUrl) return;
    setDownloading(true);
    setDownloadError(false);

    const W = 1080;
    const H = 1920;
    const PAD = 64;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#10131a';
      ctx.fillRect(0, 0, W, H);

      const imgObjUrl = await fetchBlob(imageUrl);
      try {
        const img = await loadImage(imgObjUrl);
        const areaH = 1180;
        const scale = Math.max(W / img.width, areaH / img.height);
        const iw = img.width * scale;
        const ih = img.height * scale;
        ctx.drawImage(img, (W - iw) / 2, (areaH - ih) / 2, iw, ih);
      } finally {
        URL.revokeObjectURL(imgObjUrl);
      }

      const gradient = ctx.createLinearGradient(0, 760, 0, 1240);
      gradient.addColorStop(0, 'rgba(16,19,26,0)');
      gradient.addColorStop(1, '#10131a');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 760, W, 480);

      await document.fonts.ready;
      let y = 1040;
      ctx.fillStyle = '#ffffff';
      ctx.font = '900 74px system-ui,-apple-system,sans-serif';
      ctx.fillText(truncate(ctx, celebrity.toUpperCase(), W - PAD * 2), PAD, y);
      y += 62;

      ctx.fillStyle = 'rgba(255,255,255,0.72)';
      ctx.font = '600 30px system-ui,-apple-system,sans-serif';
        ctx.fillText(truncate(ctx, totalLabel || t('lookShare.noTotal'), W - PAD * 2), PAD, y);
      y += 70;

      for (const pick of picks) {
        const price = formatPrice(pick.candidate);
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.font = '700 30px system-ui,-apple-system,sans-serif';
        ctx.fillText(truncate(ctx, pick.candidate.name, W - PAD * 2 - 170), PAD, y);
        ctx.fillStyle = '#ffffff';
        ctx.font = '900 30px system-ui,-apple-system,sans-serif';
        const pw = ctx.measureText(price).width;
        ctx.fillText(price, W - PAD - pw, y);
        y += 50;
      }

      const logoObjUrl = await fetchBlob('/brand/outfit-detective-mark-512.png');
      try {
        const logo = await loadImage(logoObjUrl);
        ctx.drawImage(logo, PAD, H - 138, 52, 52);
      } finally {
        URL.revokeObjectURL(logoObjUrl);
      }
      ctx.fillStyle = 'rgba(255,255,255,0.58)';
      ctx.font = '700 26px system-ui,-apple-system,sans-serif';
      ctx.fillText('OUTFIT DETECTIVE', PAD + 70, H - 104);

      canvas.toBlob((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `outfit-detective-story-${celebrity.replace(/\s+/g, '-').toLowerCase()}.png`;
        link.click();
      }, 'image/png');
    } catch (err) {
      console.error('Look share card generation failed', err);
      setDownloadError(true);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="ct-share-card-overlay" onClick={onClose}>
      <div className="ct-look-share-sheet" onClick={(event) => event.stopPropagation()}>
        <div className="ct-share-card-sheet-header">
          <span>{t('lookShare.title')}</span>
          <button type="button" className="ct-share-card-sheet-close" onClick={onClose} aria-label={t('aiDialog.close')}>
            <i className="ph ph-x"></i>
          </button>
        </div>
        <div className="ct-look-share-preview">
          <img src={imageUrl} alt="" />
          <div>
            <p>{celebrity}</p>
            <strong>{totalLabel || t('lookShare.noTotal')}</strong>
            {picks.map(({ candidate }) => (
              <span key={candidate.id}>
                {candidate.image_url ? <img src={productImageUrl(candidate.image_url)} alt="" /> : null}
                <em>{candidate.name}</em>
                <b>{formatPrice(candidate)}</b>
              </span>
            ))}
          </div>
        </div>
        {downloadError ? <p className="ct-share-card-error">{t('shareCard.downloadError')}</p> : null}
        <button type="button" className="btn btn-dark ct-share-card-download" onClick={downloadStory} disabled={downloading}>
          <i className="ph ph-download-simple"></i>
          {downloading ? t('shareCard.generating') : t('lookShare.download')}
        </button>
        <p className="ct-share-card-hint">{t('lookShare.hint')}</p>
      </div>
    </div>
  );
}
